import React, { useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import {
  CloudUpload,
  Download,
  Visibility,
} from '@mui/icons-material';
import { FileIcon, defaultStyles } from 'react-file-icon';
import { getBackendBaseUrl } from '@/setup/apiBaseUrl';
import { sharedFormStyles } from '@/__core/styles/sharedFormStyles';

interface FileDocument {
  id: string;
  name: string;
  file_url?: string;
  mime_type?: string;
  file_size?: number;
}

interface FileAttachmentFieldProps {
  documents?: FileDocument[];
  selectedFiles?: File[];
  disabled?: boolean;
  loading?: boolean;
  accept?: string;
  fileInputKey?: string;
  onFileSelect?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile?: (index: number) => void;
  onRemoveDocument?: (documentId: string) => void;
  onPreview?: (documentId: string) => void;
  onDownload?: (documentId: string) => void;
}

const getExt = (fileName: string, fileUrl?: string): string => {
  return (fileName || '').split('.').pop()?.toLowerCase()
    || (fileUrl || '').split('.').pop()?.split('?')[0].toLowerCase()
    || '';
};

const isImageFile = (fileName: string, mimeType?: string): boolean => {
  if (mimeType?.startsWith('image/')) return true;
  const ext = getExt(fileName);
  return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'tiff'].includes(ext);
};

const resolveFileUrl = (url?: string): string | undefined => {
  if (!url) return undefined;
  if (/^https?:\/\//i.test(url) || /^data:/i.test(url)) return url;
  return `${getBackendBaseUrl()}${url}`;
};

export default function FileAttachmentField({
  documents = [],
  selectedFiles = [],
  disabled = false,
  loading = false,
  accept = '.txt,.png,.jpg,.jpeg,.pdf,.doc,.docx,.zip,.rar,.mp4,.mp3,.avi,.mov,.gif,.bmp,.tiff,.svg,.webp',
  fileInputKey,
  onFileSelect,
  onRemoveFile,
  onRemoveDocument,
  onPreview,
  onDownload,
}: FileAttachmentFieldProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const selectedFilePreviews = useMemo(
    () => selectedFiles.map((file) => ({
      file,
      url: isImageFile(file.name, file.type) ? URL.createObjectURL(file) : undefined,
    })),
    [selectedFiles],
  );

  useEffect(() => {
    return () => {
      selectedFilePreviews.forEach(({ url }) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [selectedFilePreviews]);

  const triggerFileInput = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            flex: 1,
            minHeight: 56,
            position: 'relative',
            alignItems: 'flex-start',
          }}
        >
          {documents.length > 0 && (
            documents.map((doc) => {
              const isImage = isImageFile(doc.name || '', doc.mime_type);
              const imgSrc = isImage ? resolveFileUrl(doc.file_url) : undefined;

              return (
                <Box
                  key={doc.id}
                  sx={{
                    position: 'relative',
                    width: 56,
                    height: 56,
                    borderRadius: '6px',
                    overflow: 'hidden',
                    border: '1px solid #e5e7eb',
                    backgroundColor: '#f9fafb',
                    flexShrink: 0,
                    '&:hover .file-actions-overlay': { opacity: 1 },
                    '&:hover .file-delete-btn': { opacity: 1 },
                  }}
                >
                  {isImage && imgSrc ? (
                    <Box
                      component="img"
                      src={imgSrc}
                      alt={doc.name}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <FileIcon
                        size={36}
                        extension={getExt(doc.name || '', doc.file_url)}
                        {...(defaultStyles[getExt(doc.name || '', doc.file_url) as keyof typeof defaultStyles] || {})}
                        radius={3}
                      />
                    </Box>
                  )}
                  <Box
                    className="file-actions-overlay"
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      display: 'flex',
                      justifyContent: 'center',
                      gap: 0.25,
                      py: 0.25,
                      background: 'linear-gradient(transparent, rgba(0,0,0,0.5))',
                      opacity: 0,
                      transition: 'opacity 0.2s ease-in-out',
                    }}
                  >
                    <Box
                      onClick={() => onPreview?.(doc.id)}
                      sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', borderRadius: 1,
                        bgcolor: 'rgba(255,255,255,0.9)', color: '#374151',
                        width: 22, height: 22,
                        '&:hover': { bgcolor: '#fff', color: '#3b82f6' },
                      }}
                    >
                      <Visibility sx={{ fontSize: 14 }} />
                    </Box>
                    <Box
                      onClick={() => onDownload?.(doc.id)}
                      sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', borderRadius: 1,
                        bgcolor: 'rgba(255,255,255,0.9)', color: '#374151',
                        width: 22, height: 22,
                        '&:hover': { bgcolor: '#fff', color: '#22c55e' },
                      }}
                    >
                      <Download sx={{ fontSize: 14 }} />
                    </Box>
                  </Box>
                  {!disabled && (
                    <IconButton
                      size="small"
                      onClick={() => onRemoveDocument?.(doc.id)}
                      className="file-delete-btn"
                      sx={{
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        p: 0, width: 18, height: 18, minWidth: 18,
                        borderRadius: '999px',
                        bgcolor: 'rgba(255,255,255,0.9)',
                        color: '#dc2626',
                        fontSize: '10px',
                        fontWeight: 700,
                        opacity: 0,
                        transition: 'opacity 0.2s ease-in-out',
                        '&:hover': { bgcolor: '#fff' },
                      }}
                    >
                      x
                    </IconButton>
                  )}
                </Box>
              );
            })
          )}

          {selectedFilePreviews.map(({ file, url: newImgSrc }, index) => {
            const isNewImage = isImageFile(file.name, file.type);

            return (
              <Box
                key={`new-${index}`}
                sx={{
                  position: 'relative',
                  width: 56,
                  height: 56,
                  borderRadius: '6px',
                  overflow: 'hidden',
                  border: '1px solid #bfdbfe',
                  backgroundColor: '#eff6ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  '&:hover .file-delete-btn': { opacity: 1 },
                }}
              >
                {isNewImage && newImgSrc ? (
                  <Box
                    component="img"
                    src={newImgSrc}
                    alt={file.name}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <CloudUpload sx={{ fontSize: 28, color: '#3b82f6' }} />
                )}
                {!disabled && (
                  <IconButton
                    size="small"
                    onClick={() => onRemoveFile?.(index)}
                    className="file-delete-btn"
                    sx={{
                      position: 'absolute',
                      top: 2,
                      right: 2,
                      p: 0, width: 18, height: 18, minWidth: 18,
                      borderRadius: '999px',
                      bgcolor: 'rgba(255,255,255,0.9)',
                      color: '#dc2626',
                      fontSize: '10px',
                      fontWeight: 700,
                      opacity: 0,
                      transition: 'opacity 0.2s ease-in-out',
                      '&:hover': { bgcolor: '#fff' },
                    }}
                  >
                    x
                  </IconButton>
                )}
              </Box>
            );
          })}

          {loading && (
            <Box
              sx={{
                ...sharedFormStyles.fileLoadingBox,
                position: 'absolute',
                inset: 0,
                minHeight: 56,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1,
                bgcolor: 'rgba(255,255,255,0.72)',
                pointerEvents: 'none',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Đang tải files...
              </Typography>
            </Box>
          )}
        </Box>

        <CloudUpload
          sx={{
            ...sharedFormStyles.fileUploadIcon,
            flexShrink: 0,
          }}
          onClick={triggerFileInput}
        />
      </Box>

      <input
        key={fileInputKey || 'file-input'}
        type="file"
        ref={fileInputRef}
        onChange={onFileSelect}
        disabled={disabled}
        style={sharedFormStyles.hiddenFileInput}
        multiple
        accept={accept}
      />
    </>
  );
}
